import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { useId } from 'react'

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string | null
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode
}) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(' ')

  return (
    <div className="mx-field">
      <label className="mx-field-label" htmlFor={id}>
        {label}
      </label>
      {hint && (
        <span className="mx-field-hint" id={hintId}>
          {hint}
        </span>
      )}
      {children({
        id,
        ...(error ? { 'aria-invalid': true } : {}),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      })}
      {error && (
        <span className="mx-field-error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={['mx-input', className].filter(Boolean).join(' ')} {...rest} />
}

export function TextArea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={['mx-textarea', className].filter(Boolean).join(' ')} {...rest} />
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={['mx-select', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </select>
  )
}

/** For anything a student picks several of. `aria-pressed` carries the state the
 *  fill is showing, so a screen reader announces it too. */
export function Chip({
  selected,
  onToggle,
  children,
  disabled,
}: {
  selected: boolean
  onToggle: () => void
  children: ReactNode
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      className="mx-chip"
      aria-pressed={selected}
      onClick={onToggle}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

/**
 * A switch takes effect immediately — there is no Save button beside one. If a
 * setting needs confirming, it is not a switch.
 */
export function Switch({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  hint?: string
  disabled?: boolean
}) {
  return (
    <div className="mx-switch-row">
      <div>
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', fontWeight: 600 }}>{label}</p>
        {hint && (
          <p style={{ margin: '2px 0 0', fontSize: 'var(--text-xs)', color: 'var(--muted)' }}>
            {hint}
          </p>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        className="mx-switch"
        disabled={disabled}
        onClick={() => onChange(!checked)}
      />
    </div>
  )
}

/** For switching a view, never for choosing a value a form will submit. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (next: T) => void
  label: string
}) {
  return (
    <div className="mx-segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
